import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plane, MapPin, Package, Sparkles } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { ALL_COUNTRIES, STATS, SERVICES } from "@/data/turboData";

const QuoteWidget = () => {
  const nav = useNavigate();
  const [from, setFrom] = useState("United States");
  const [to, setTo] = useState("Nigeria");
  const [weight, setWeight] = useState("");
  const [service, setService] = useState(SERVICES[0].slug);

  const submit = (e) => {
    e.preventDefault();
    const q = new URLSearchParams({ from, to, weight, service });
    nav(`/quote?${q.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      className="relative w-full max-w-xl rounded-2xl bg-white/95 backdrop-blur-md border border-[var(--tt-line)] p-5 shadow-2xl shadow-[var(--tt-navy)]/15"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--tt-accent)]/10 text-[var(--tt-accent)]">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--tt-muted)]">
          Instant quote · 30 seconds
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">From</span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-[var(--tt-line)] px-3 py-2.5 focus-within:border-[var(--tt-navy)] transition">
            <MapPin className="h-4 w-4 text-[var(--tt-accent)]" />
            <select
              data-testid={HERO.quoteFrom}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full bg-transparent outline-none text-sm font-medium text-[var(--tt-ink)]"
            >
              {ALL_COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">To</span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-[var(--tt-line)] px-3 py-2.5 focus-within:border-[var(--tt-navy)] transition">
            <Plane className="h-4 w-4 text-[var(--tt-accent)]" />
            <select
              data-testid={HERO.quoteTo}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full bg-transparent outline-none text-sm font-medium text-[var(--tt-ink)]"
            >
              {ALL_COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">Weight (kg)</span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-[var(--tt-line)] px-3 py-2.5 focus-within:border-[var(--tt-navy)] transition">
            <Package className="h-4 w-4 text-[var(--tt-accent)]" />
            <input
              data-testid={HERO.quoteWeight}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              type="number"
              min="0"
              placeholder="e.g. 12"
              className="w-full bg-transparent outline-none text-sm font-medium text-[var(--tt-ink)] placeholder:text-[var(--tt-muted)]/60"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">Service</span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-[var(--tt-line)] px-3 py-2.5 focus-within:border-[var(--tt-navy)] transition">
            <select
              data-testid={HERO.quoteService}
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full bg-transparent outline-none text-sm font-medium text-[var(--tt-ink)]"
            >
              {SERVICES.map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </div>
        </label>
      </div>

      <button
        type="submit"
        data-testid={HERO.quoteSubmit}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--tt-navy)] hover:bg-[var(--tt-navy-2)] text-white font-semibold py-3.5 transition group"
      >
        Get my price
        <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />
      </button>
      <p className="mt-2 text-[11px] text-center text-[var(--tt-muted)]">
        No account required · No commitment
      </p>
    </form>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden">
    {/* Decorative grain background */}
    <div className="absolute inset-0 grain pointer-events-none" />
    {/* Orange wedge */}
    <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-[var(--tt-accent)]/12 blur-3xl pointer-events-none" />
    <div className="absolute top-40 left-10 w-[420px] h-[420px] rounded-full bg-[var(--tt-accent-2)]/15 blur-3xl pointer-events-none" />

    <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 md:pt-16 pb-20">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--tt-line)] bg-white/60 backdrop-blur px-3 py-1.5 text-xs font-medium text-[var(--tt-ink-soft)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent)] pulse-dot" />
            Trusted by shippers in 180+ countries
          </div>

          <h1 className="font-display mt-5 text-[42px] sm:text-6xl lg:text-[76px] leading-[0.95] font-bold text-[var(--tt-navy)] tracking-tight">
            Your package,
            <br />
            on a <span className="relative inline-block">
              <span className="relative z-10 text-[var(--tt-accent)]">faster</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-[var(--tt-accent-2)]/45 -z-0 rounded-sm" />
            </span> route.
          </h1>

          <p className="mt-6 text-lg text-[var(--tt-ink-soft)] max-w-xl leading-relaxed">
            Global door-to-door shipping for individuals and businesses.
            Air, sea, suitcase, or barrel — we&apos;ll get it there in 7 days or less
            with real-time tracking the whole way.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/quote" data-testid={HERO.primaryCta} className="btn-primary">
              Get an instant quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid={HERO.secondaryCta} className="btn-ghost">
              See all services
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-y-5 gap-x-6 max-w-xl">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display text-2xl md:text-3xl font-bold text-[var(--tt-navy)]">{s.num}</div>
                <div className="mt-1 text-xs text-[var(--tt-muted)] leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <QuoteWidget />
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
